'use client';

import { c as _c } from "react/compiler-runtime";
import React from 'react';
import { useModal, ModalConfig } from './modal-context';

/**
 * Props the app's sign-in component must accept so the shared sign-in modal helpers
 * can drive it. Each app registers its own implementation via
 * {@link registerSignInComponent} — the shared package never imports a concrete
 * SignIn component, which is what keeps this file app-agnostic.
 */
export interface SignInComponentProps {
  onSuccess?: () => void;
  redirectTo?: string;
  preferFullscreenForNewUsers?: boolean;
  onBeforeRouteChange?: () => void;
  initialEmail?: string;
  autoSubmitEmail?: boolean;
}
let SignInComponent: React.ComponentType<SignInComponentProps> | null = null;

/**
 * Register the app's sign-in component. Call this once during bootstrap (e.g. from
 * the app's provider tree) before any sign-in modal is opened.
 */
export function registerSignInComponent(component: React.ComponentType<SignInComponentProps>) {
  SignInComponent = component;
}
function renderSignIn(props: SignInComponentProps): React.ReactNode {
  if (!SignInComponent) {
    console.error('No sign-in component registered. Call registerSignInComponent() during app bootstrap.');
    return null;
  }
  return <SignInComponent {...props} />;
}
export interface SignInModalOptions {
  onSuccess?: () => void;
  redirectTo?: string;
  title?: string;
  description?: React.ReactNode;
  preferFullscreenForNewUsers?: boolean;
  initialEmail?: string;
  autoSubmitEmail?: boolean;
  preventAutoFocus?: boolean;
}
const SIGN_IN_MODAL_CLASS_NAME = 'overflow-y-auto p-0 sm:w-[min(96vw,44rem)] sm:max-w-[44rem]';

// Generic modal helper hook
export function useModalHelpers() {
  const $ = _c(10);
  const {
    showModal,
    hideModal,
    hideAllModals
  } = useModal();
  let t0;
  if ($[0] !== showModal) {
    t0 = config => showModal(config);
    $[0] = showModal;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const showGenericModal = t0;
  let t1;
  if ($[2] !== hideModal || $[3] !== showModal) {
    t1 = (title, message, onConfirm, onCancel) => {
      const modalId = showModal({
        title,
        description: message,
        component: <div className="flex gap-2 justify-end mt-4"><button className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 cursor-pointer" onClick={() => {
            onCancel?.();
            hideModal(modalId);
          }}>Cancel</button><button className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 cursor-pointer" onClick={() => {
            onConfirm();
            hideModal(modalId);
          }}>Confirm</button></div>
      });
      return modalId;
    };
    $[2] = hideModal;
    $[3] = showModal;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const showConfirmModal = t1;
  let t2;
  if ($[5] !== hideAllModals || $[6] !== hideModal || $[7] !== showConfirmModal || $[8] !== showGenericModal) {
    t2 = {
      showModal: showGenericModal,
      hideModal,
      hideAllModals,
      showConfirmModal
    };
    $[5] = hideAllModals;
    $[6] = hideModal;
    $[7] = showConfirmModal;
    $[8] = showGenericModal;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
}

// Specific modal helpers
export function useSignInModal() {
  const $ = _c(6);
  const {
    showModal,
    hideModal
  } = useModal();
  let t0;
  if ($[0] !== hideModal || $[1] !== showModal) {
    t0 = options => {
      const modalId = showModal({
        title: options?.title,
        description: options?.description,
        showCloseButton: true,
        fullscreenOnMobile: true,
        preventAutoFocus: options?.preventAutoFocus ?? false,
        component: renderSignIn({
          onSuccess: () => {
            options?.onSuccess?.();
            hideModal(modalId);
          },
          redirectTo: options?.redirectTo,
          preferFullscreenForNewUsers: options?.preferFullscreenForNewUsers ?? true,
          onBeforeRouteChange: () => hideModal(modalId),
          initialEmail: options?.initialEmail,
          autoSubmitEmail: options?.autoSubmitEmail
        }),
        className: SIGN_IN_MODAL_CLASS_NAME
      });
      return modalId;
    };
    $[0] = hideModal;
    $[1] = showModal;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const showSignInModal = t0;
  let t1;
  if ($[3] !== hideModal || $[4] !== showSignInModal) {
    t1 = {
      showSignInModal,
      hideModal
    };
    $[3] = hideModal;
    $[4] = showSignInModal;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
}

// Export a simple function to show sign-in modal from anywhere
let modalContext: ReturnType<typeof useModal> | null = null;
export function setModalContext(context: ReturnType<typeof useModal>) {
  modalContext = context;
}
export function showSignInModal(options?: SignInModalOptions) {
  if (!modalContext) {
    console.error('Modal context not initialized. Make sure ModalProvider is set up.');
    return;
  }
  const modalId = modalContext.showModal({
    title: options?.title,
    description: options?.description,
    showCloseButton: true,
    fullscreenOnMobile: true,
    preventAutoFocus: options?.preventAutoFocus ?? true,
    component: renderSignIn({
      onSuccess: () => {
        options?.onSuccess?.();
        if (modalId) {
          modalContext.hideModal(modalId);
        }
      },
      redirectTo: options?.redirectTo,
      preferFullscreenForNewUsers: options?.preferFullscreenForNewUsers ?? true,
      onBeforeRouteChange: () => {
        if (modalId) {
          modalContext.hideModal(modalId);
        }
      },
      initialEmail: options?.initialEmail,
      autoSubmitEmail: options?.autoSubmitEmail
    }),
    className: SIGN_IN_MODAL_CLASS_NAME
  });
  return modalId;
}